'use client';

import Link from '@/components/NoPrefetchLink';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import { Suspense, useEffect, useState } from 'react';
import { formatBirthday, translatePosition, translateNationality } from '@/lib/format';
import { withLocalePrefix } from '@/lib/localePath';
import type { Locale } from '@/lib/localePath';
import PageEmptyState from '@/components/PageEmptyState';
import { TEAM_COLORS } from '@/lib/teamColors';
import type { TeamPlayer, TeamPlayersPayload } from '@/lib/teamPlayer';
import { compactTeamPlayersPayload } from '@/lib/teamPlayer';
import '@/styles/InfoPage.css';
import '@/styles/Team.css';

type Props = { locale: Locale; teamSlug: string; league: 'npb' | 'mlb'; initialPlayers?: TeamPlayersPayload };
type SearchParamValues = { q: string; pos: string | null; active: string | null; ob: string | null };
type SortField = 'uniformNumber' | 'name' | 'position' | 'nationality' | 'birthday';
type SortDirection = 'asc' | 'desc';

const DEFAULT_SPV: SearchParamValues = { q: '', pos: null, active: null, ob: null };

const POSITION_ORDER: Record<string, number> = {
    '投手': 0, 'P': 0, 'pitcher': 0,
    '捕手': 1, 'C': 1, 'catcher': 1,
    '一塁手': 2, '一塁': 2, '1B': 2, 'first_base': 2,
    '二塁手': 3, '二塁': 3, '2B': 3, 'second_base': 3,
    '三塁手': 4, '三塁': 4, '3B': 4, 'third_base': 4,
    '遊撃手': 5, '遊撃': 5, 'SS': 5, 'shortstop': 5,
    '内野手': 6, 'IF': 6, 'infield': 6,
    '左翼手': 7, '左翼': 7, 'LF': 7, 'left_field': 7,
    '中堅手': 8, '中堅': 8, 'CF': 8, 'center_field': 8,
    '右翼手': 9, '右翼': 9, 'RF': 9, 'right_field': 9,
    '外野手': 10, '外野': 10, 'OF': 10, 'outfield': 10,
    '指名打者': 11, 'DH': 11, 'designated_hitter': 11,
};

const positionRank = (pos: string | null | undefined): number => {
    if (!pos) return 99;
    const first = pos.split(/[,、/\s]/)[0].trim();
    return POSITION_ORDER[first] ?? 99;
};

const toHiragana = (str: string): string =>
    str.replace(/[\u30A1-\u30F6]/g, (m) => String.fromCharCode(m.charCodeAt(0) - 0x60));

const POS_P = '投手';
const POS_C = '捕手';
const POS_IF = '内野手';
const POS_OF = '外野手';
const POS_DH = '指名打者';
const POS_1B = '一塁手';
const POS_2B = '二塁手';
const POS_3B = '三塁手';
const POS_SS = '遊撃手';
const POS_ALL_LABEL = 'すべて';
const DEFAULT_POSITION = 'all';
const POSITION_OPTIONS = [POS_P, POS_C, POS_1B, POS_2B, POS_3B, POS_SS, POS_OF, POS_DH] as const;
const TEAM_PAGE_SIZE = 20;

const teamNameJaBySlug: Record<string, string> = {
    yomiuri_giants: '読売ジャイアンツ',
    hanshin_tigers: '阪神タイガース',
    chunichi_dragons: '中日ドラゴンズ',
    hiroshima_toyo_carp: '広島東洋カープ',
    tokyo_yakult_swallows: '東京ヤクルトスワローズ',
    yokohama_dena_baystars: '横浜DeNAベイスターズ',
    fukuoka_softbank_hawks: '福岡ソフトバンクホークス',
    hokkaido_nipponham_fighters: '北海道日本ハムファイターズ',
    chiba_lotte_marines: '千葉ロッテマリーンズ',
    saitama_seibu_lions: '埼玉西武ライオンズ',
    orix_buffaloes: 'オリックス・バファローズ',
    tohoku_rakuten_golden_eagles: '東北楽天ゴールデンイーグルス',
};

const npbTeamNamesEn: Record<string, string> = {
    yomiuri_giants: 'Yomiuri Giants',
    hanshin_tigers: 'Hanshin Tigers',
    chunichi_dragons: 'Chunichi Dragons',
    hiroshima_toyo_carp: 'Hiroshima Toyo Carp',
    tokyo_yakult_swallows: 'Tokyo Yakult Swallows',
    yokohama_dena_baystars: 'Yokohama DeNA BayStars',
    fukuoka_softbank_hawks: 'Fukuoka SoftBank Hawks',
    hokkaido_nipponham_fighters: 'Hokkaido Nippon-Ham Fighters',
    chiba_lotte_marines: 'Chiba Lotte Marines',
    saitama_seibu_lions: 'Saitama Seibu Lions',
    orix_buffaloes: 'Orix Buffaloes',
    tohoku_rakuten_golden_eagles: 'Tohoku Rakuten Golden Eagles',
};

const mlbTeamNamesJa: Record<string, string> = {
    arizona_diamondbacks: 'アリゾナ・ダイヤモンドバックス',
    atlanta_braves: 'アトランタ・ブレーブス',
    baltimore_orioles: 'ボルチモア・オリオールズ',
    boston_red_sox: 'ボストン・レッドソックス',
    chicago_cubs: 'シカゴ・カブス',
    chicago_white_sox: 'シカゴ・ホワイトソックス',
    cincinnati_reds: 'シンシナティ・レッズ',
    cleveland_guardians: 'クリーブランド・ガーディアンズ',
    colorado_rockies: 'コロラド・ロッキーズ',
    detroit_tigers: 'デトロイト・タイガース',
    houston_astros: 'ヒューストン・アストロズ',
    kansas_city_royals: 'カンザスシティ・ロイヤルズ',
    los_angeles_angels: 'ロサンゼルス・エンゼルス',
    los_angeles_dodgers: 'ロサンゼルス・ドジャース',
    miami_marlins: 'マイアミ・マーリンズ',
    milwaukee_brewers: 'ミルウォーキー・ブルワーズ',
    minnesota_twins: 'ミネソタ・ツインズ',
    new_york_mets: 'ニューヨーク・メッツ',
    new_york_yankees: 'ニューヨーク・ヤンキース',
    oakland_athletics: 'オークランド・アスレチックス',
    philadelphia_phillies: 'フィラデルフィア・フィリーズ',
    pittsburgh_pirates: 'ピッツバーグ・パイレーツ',
    san_diego_padres: 'サンディエゴ・パドレス',
    san_francisco_giants: 'サンフランシスコ・ジャイアンツ',
    seattle_mariners: 'シアトル・マリナーズ',
    st_louis_cardinals: 'セントルイス・カージナルス',
    tampa_bay_rays: 'タンパベイ・レイズ',
    texas_rangers: 'テキサス・レンジャーズ',
    toronto_blue_jays: 'トロント・ブルージェイズ',
    washington_nationals: 'ワシントン・ナショナルズ',
};

const mlbTeamNamesEn: Record<string, string> = {
    arizona_diamondbacks: 'Arizona Diamondbacks',
    atlanta_braves: 'Atlanta Braves',
    baltimore_orioles: 'Baltimore Orioles',
    boston_red_sox: 'Boston Red Sox',
    chicago_cubs: 'Chicago Cubs',
    chicago_white_sox: 'Chicago White Sox',
    cincinnati_reds: 'Cincinnati Reds',
    cleveland_guardians: 'Cleveland Guardians',
    colorado_rockies: 'Colorado Rockies',
    detroit_tigers: 'Detroit Tigers',
    houston_astros: 'Houston Astros',
    kansas_city_royals: 'Kansas City Royals',
    los_angeles_angels: 'Los Angeles Angels',
    los_angeles_dodgers: 'Los Angeles Dodgers',
    miami_marlins: 'Miami Marlins',
    milwaukee_brewers: 'Milwaukee Brewers',
    minnesota_twins: 'Minnesota Twins',
    new_york_mets: 'New York Mets',
    new_york_yankees: 'New York Yankees',
    oakland_athletics: 'Oakland Athletics',
    philadelphia_phillies: 'Philadelphia Phillies',
    pittsburgh_pirates: 'Pittsburgh Pirates',
    san_diego_padres: 'San Diego Padres',
    san_francisco_giants: 'San Francisco Giants',
    seattle_mariners: 'Seattle Mariners',
    st_louis_cardinals: 'St. Louis Cardinals',
    tampa_bay_rays: 'Tampa Bay Rays',
    texas_rangers: 'Texas Rangers',
    toronto_blue_jays: 'Toronto Blue Jays',
    washington_nationals: 'Washington Nationals',
};

const mlbTeamNamesEs: Record<string, string> = {
    arizona_diamondbacks: 'Diamondbacks de Arizona',
    atlanta_braves: 'Bravos de Atlanta',
    baltimore_orioles: 'Orioles de Baltimore',
    boston_red_sox: 'Medias Rojas de Boston',
    chicago_cubs: 'Cachorros de Chicago',
    chicago_white_sox: 'Medias Blancas de Chicago',
    cincinnati_reds: 'Rojos de Cincinnati',
    cleveland_guardians: 'Guardianes de Cleveland',
    colorado_rockies: 'Rockies de Colorado',
    detroit_tigers: 'Tigres de Detroit',
    houston_astros: 'Astros de Houston',
    kansas_city_royals: 'Reales de Kansas City',
    los_angeles_angels: 'Angelinos de Los Ángeles',
    los_angeles_dodgers: 'Dodgers de Los Ángeles',
    miami_marlins: 'Marlins de Miami',
    milwaukee_brewers: 'Cerveceros de Milwaukee',
    minnesota_twins: 'Mellizos de Minnesota',
    new_york_mets: 'Mets de Nueva York',
    new_york_yankees: 'Yankees de Nueva York',
    oakland_athletics: 'Atléticos de Oakland',
    philadelphia_phillies: 'Filis de Filadelfia',
    pittsburgh_pirates: 'Piratas de Pittsburgh',
    san_diego_padres: 'Padres de San Diego',
    san_francisco_giants: 'Gigantes de San Francisco',
    seattle_mariners: 'Marineros de Seattle',
    st_louis_cardinals: 'Cardenales de San Luis',
    tampa_bay_rays: 'Rays de Tampa Bay',
    texas_rangers: 'Rangers de Texas',
    toronto_blue_jays: 'Azulejos de Toronto',
    washington_nationals: 'Nacionales de Washington',
};

function TeamInner({ locale, teamSlug, league, initialPlayers, spv }: Props & { spv: SearchParamValues }) {
    const router = useRouter();
    const pathname = usePathname();

    let initialStatus: 'active' | 'ob' | 'all' = 'active';
    if (spv.active === '1' && spv.ob === '1') initialStatus = 'all';
    else if (spv.active === '0' && spv.ob === '1') initialStatus = 'ob';

    const [activePlayers, setActivePlayers] = useState<TeamPlayer[]>(initialPlayers?.active ?? []);
    const [obPlayers, setObPlayers] = useState<TeamPlayer[]>(initialPlayers?.ob ?? []);
    const [teamInfo, setTeamInfo] = useState<{ official_name?: string }>(initialPlayers?.team ?? {});
    const [loading, setLoading] = useState(false);
    const [filterQuery, setFilterQuery] = useState(spv.q);
    const [appliedFilter, setAppliedFilter] = useState(spv.q);
    const [isComposing, setIsComposing] = useState(false);
    const [rosterStatus, setRosterStatus] = useState<'active' | 'ob' | 'all'>(initialStatus);
    const [selectedPosition, setSelectedPosition] = useState<string>(spv.pos || DEFAULT_POSITION);
    const [teamPage, setTeamPage] = useState(1);
    const [sortField, setSortField] = useState<SortField>(initialStatus === 'active' ? 'uniformNumber' : 'name');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

    const t = (ja: string, en: string, es?: string) => {
        if (locale === 'ja') return ja;
        if (locale === 'es') return es || en;
        return en;
    };

    const getTeamName = () => {
        if (locale === 'ja') {
            return teamInfo.official_name || teamNameJaBySlug[teamSlug] || mlbTeamNamesJa[teamSlug] || teamSlug.toUpperCase();
        } else if (locale === 'es') {
            return mlbTeamNamesEs[teamSlug] || npbTeamNamesEn[teamSlug] || teamNameJaBySlug[teamSlug] || teamSlug.toUpperCase();
        }
        return npbTeamNamesEn[teamSlug] || mlbTeamNamesEn[teamSlug] || teamNameJaBySlug[teamSlug] || teamSlug.toUpperCase();
    };

    const teamName = getTeamName();
    const leagueLabel = league.toUpperCase();
    const teamBasePath = withLocalePrefix(locale, `/${league}/${teamSlug}`);
    const leagueHomePath = withLocalePrefix(locale, `/${league}`);

    const getPositionShortLabel = (position: string) => {
        if (position === POS_ALL_LABEL) return 'All';
        if (position === POS_P) return 'P';
        if (position === POS_C) return 'C';
        if (position === POS_IF) return 'IF';
        if (position === POS_1B) return '1B';
        if (position === POS_2B) return '2B';
        if (position === POS_3B) return '3B';
        if (position === POS_SS) return 'SS';
        if (position === POS_OF) return 'OF';
        return 'DH';
    };

    const val = (ja: string | null | undefined, en: string | null | undefined) => {
        if (locale === 'en' && en) return en;
        if (locale === 'es' && en) return en;
        if (ja) return ja;
        return en || '-';
    };
    const valStrict = (ja: string | null | undefined, en: string | null | undefined) => {
        if (locale === 'en' || locale === 'es') return en || '-';
        return ja || '-';
    };
    const getDisplayedPosition = (player: TeamPlayer) =>
        valStrict(translatePosition(player.position, locale), translatePosition(player.position_en, locale));
    const getDisplayedNationality = (player: TeamPlayer) =>
        locale === 'es'
            ? (player.nationality_es || player.nationality_en || '-')
            : valStrict(translateNationality(player.nationality, locale), translateNationality(player.nationality_en, locale));
    const getUniformNumber = (player: TeamPlayer) => {
        const value = (player.uniform_number ?? '').trim();
        return value.length > 0 ? value : null;
    };
    const normalizeSortText = (text: string | null | undefined) => toHiragana((text || '').trim()).toLowerCase();
    const collator = new Intl.Collator(locale === 'ja' ? 'ja' : locale === 'es' ? 'es' : 'en', {
        numeric: true,
        sensitivity: 'base',
    });
    const compareText = (a: string | null | undefined, b: string | null | undefined) => {
        const aa = normalizeSortText(a);
        const bb = normalizeSortText(b);
        if (!aa && !bb) return 0;
        if (!aa) return 1;
        if (!bb) return -1;
        return collator.compare(aa, bb);
    };
    const compareBirthday = (a: TeamPlayer, b: TeamPlayer) => {
        const aTime = a.birthday_date ? Date.parse(a.birthday_date) : NaN;
        const bTime = b.birthday_date ? Date.parse(b.birthday_date) : NaN;
        const aHas = Number.isFinite(aTime);
        const bHas = Number.isFinite(bTime);
        if (!aHas && !bHas) return 0;
        if (!aHas) return 1;
        if (!bHas) return -1;
        return aTime - bTime;
    };
    const compareUniformNumber = (a: TeamPlayer, b: TeamPlayer) => {
        const aa = getUniformNumber(a);
        const bb = getUniformNumber(b);
        if (!aa && !bb) return 0;
        if (!aa) return 1;
        if (!bb) return -1;
        return collator.compare(aa, bb);
    };
    const handleSort = (field: SortField) => {
        setTeamPage(1);
        if (sortField === field) {
            setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
            return;
        }
        setSortField(field);
        setSortDirection('asc');
    };
    const getSortIndicator = (field: SortField) => {
        if (sortField !== field) return '';
        return sortDirection === 'asc' ? '\u25B2' : '\u25BC';
    };
    const getAriaSort = (field: SortField): 'none' | 'ascending' | 'descending' => {
        if (sortField !== field) return 'none';
        return sortDirection === 'asc' ? 'ascending' : 'descending';
    };

    const updateSearchParams = (params: Record<string, string | null>) => {
        const currentSearch = window.location.search;
        const basePath = pathname ?? window.location.pathname;
        const currentQuery = new URLSearchParams(currentSearch).toString();
        const next = new URLSearchParams(currentSearch);
        for (const [k, v] of Object.entries(params)) {
            if (v === null) next.delete(k);
            else next.set(k, v);
        }
        const nextQuery = next.toString();
        if (nextQuery === currentQuery) return;
        router.replace(nextQuery ? `${basePath}?${nextQuery}` : basePath, { scroll: false });
    };

    useEffect(() => {
        setFilterQuery(spv.q);
        setAppliedFilter(spv.q);
        if (spv.active === '1' && spv.ob === '1') setRosterStatus('all');
        else if (spv.active === '0' && spv.ob === '1') setRosterStatus('ob');
        else setRosterStatus('active');
        setSelectedPosition(spv.pos || DEFAULT_POSITION);
    }, [spv.q, spv.pos, spv.active, spv.ob]);


    useEffect(() => {
        const params: Record<string, string | null> = {};
        if (rosterStatus === 'all') {
            params['active'] = '1';
            params['ob'] = '1';
        } else if (rosterStatus === 'ob') {
            params['active'] = '0';
            params['ob'] = '1';
        } else {
            params['active'] = null;
            params['ob'] = null;
        }
        params['pos'] = selectedPosition === DEFAULT_POSITION ? null : selectedPosition;
        updateSearchParams(params);
    }, [rosterStatus, selectedPosition]);

    useEffect(() => {
        if (isComposing) return;
        const timer = setTimeout(() => {
            updateSearchParams({ q: filterQuery || null });
            setAppliedFilter(filterQuery);
        }, 300);
        return () => clearTimeout(timer);
    }, [filterQuery, isComposing]);

    useEffect(() => {
        setTeamPage(1);
        setSortField(rosterStatus === 'active' ? 'uniformNumber' : 'name');
        setSortDirection('asc');
    }, [rosterStatus]);

    useEffect(() => { setTeamPage(1); }, [selectedPosition, appliedFilter]);

    if (loading) {
        return <div className="loading">Loading...</div>;
    }

    const posGroupMap: Record<string, string[]> = {
        [POS_P]: [POS_P], [POS_C]: [POS_C],
        [POS_IF]: [POS_1B, POS_2B, POS_3B, POS_SS, POS_IF],
        [POS_1B]: [POS_1B], [POS_2B]: [POS_2B], [POS_3B]: [POS_3B],
        [POS_SS]: [POS_SS], [POS_OF]: [POS_OF], [POS_DH]: [POS_DH],
    };

    const sourcePlayers: TeamPlayer[] = [];
    if (rosterStatus === 'active' || rosterStatus === 'all') sourcePlayers.push(...activePlayers);
    if (rosterStatus === 'ob' || rosterStatus === 'all') sourcePlayers.push(...obPlayers);
    const hasRosterData = sourcePlayers.length > 0;

    const activeMainPositions = selectedPosition !== 'all' ? (posGroupMap[selectedPosition] || []) : [];

    const filteredPlayers = sourcePlayers.filter((player) => {
        if (selectedPosition !== 'all') {
            const mainPosition = player.main_position?.trim() || '';
            if (!mainPosition) return false;
            const mainPosArray = mainPosition.split(/[,、/]/).map((p) => p.replace(/\s+/g, ' ').trim()).filter(Boolean);
            const isMatch = mainPosArray.some((mp) => activeMainPositions.some((amp) => mp.includes(amp) || amp.includes(mp)));
            if (!isMatch) return false;
        }
        if (!appliedFilter) return true;
        const q = toHiragana(appliedFilter.trim()).toLowerCase();
        const name = toHiragana(player.name).toLowerCase();
        const reading = toHiragana(player.name_reading || '').toLowerCase();
        const nameEn = (player.name_en || '').toLowerCase();
        return name.includes(q) || reading.includes(q) || nameEn.includes(q);
    });

    const sortedPlayers = [...filteredPlayers].sort((a, b) => {
        const getNameForSort = (player: TeamPlayer) =>
            locale === 'ja'
                ? (player.name_reading || player.name || player.name_en || '')
                : (player.name_en || player.name || '');

        let result = 0;
        if (sortField === 'uniformNumber') {
            result = compareUniformNumber(a, b);
        } else if (sortField === 'name') {
            result = compareText(getNameForSort(a), getNameForSort(b));
        } else if (sortField === 'position') {
            const pa = positionRank(a.main_position ?? a.position);
            const pb = positionRank(b.main_position ?? b.position);
            result = pa - pb;
        } else if (sortField === 'nationality') {
            result = compareText(getDisplayedNationality(a), getDisplayedNationality(b));
        } else {
            result = compareBirthday(a, b);
        }

        if (result === 0) {
            result = compareText(getNameForSort(a), getNameForSort(b));
        }
        if (result === 0) {
            result = a.profile_id - b.profile_id;
        }
        return sortDirection === 'asc' ? result : -result;
    });

    const totalTeamPages = Math.ceil(sortedPlayers.length / TEAM_PAGE_SIZE);
    const pagedPlayers = sortedPlayers.slice((teamPage - 1) * TEAM_PAGE_SIZE, teamPage * TEAM_PAGE_SIZE);
    const teamPageStart = sortedPlayers.length > 0 ? (teamPage - 1) * TEAM_PAGE_SIZE + 1 : 0;
    const teamPageEnd = sortedPlayers.length > 0 ? Math.min(teamPage * TEAM_PAGE_SIZE, sortedPlayers.length) : 0;
    const emptyStateLinks = [
        {
            href: teamBasePath,
            label: t(
                '絞り込みを解除して再表示',
                'Clear filters and show the roster again',
                'Borrar filtros y mostrar la plantilla',
            ),
            note: t('絞り込み解除。', 'Clear the filters.', 'Borra los filtros.'),
        },
        {
            href: leagueHomePath,
            label: leagueLabel === 'NPB'
                ? t('プロ野球（NPB）トップ', 'NPB home', 'Inicio NPB')
                : t('メジャーリーグ（MLB）トップ', 'MLB home', 'Inicio MLB'),
            note: t('リーグトップへ。', 'Go to the league home page.', 'Ve a la página principal de la liga.'),
        },
    ];

    const getTeamPaginationItems = (): Array<number | 'ellipsis'> => {
        if (totalTeamPages <= 7) return Array.from({ length: totalTeamPages }, (_, i) => i + 1);
        const items: Array<number | 'ellipsis'> = [1];
        const start = Math.max(2, teamPage - 1);
        const end = Math.min(totalTeamPages - 1, teamPage + 1);
        if (start > 2) items.push('ellipsis');
        for (let p = start; p <= end; p++) items.push(p);
        if (end < totalTeamPages - 1) items.push('ellipsis');
        items.push(totalTeamPages);
        return items;
    };

    const renderPagination = (position: 'top' | 'bottom') => {
        if (totalTeamPages <= 1) return null;
        return (
            <div className={`flex items-center justify-center gap-3 ${position === 'bottom' ? 'mt-3' : 'mt-0'} ${position === 'top' ? 'mb-2' : 'mb-0'}`}>
                <button
                    onClick={() => setTeamPage((p) => Math.max(1, p - 1))}
                    disabled={teamPage <= 1}
                    className={`rounded-[4px] border border-[var(--color-border-default)] bg-[var(--color-surface-card)] px-[10px] py-1 text-[12px] text-[var(--color-text-primary)] ${teamPage <= 1 ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                >
                    {t('前へ', 'Prev', 'Anterior')}
                </button>
                <div className="flex items-center gap-1">
                    {getTeamPaginationItems().map((item, idx) =>
                        item === 'ellipsis' ? (
                            <span key={`e-${idx}`} className="px-1 text-[12px] text-[var(--color-text-subtle)]">...</span>
                        ) : (
                            <button
                                key={item}
                                onClick={() => setTeamPage(item)}
                                disabled={item === teamPage}
                                className={`min-w-[28px] rounded-[4px] border border-[var(--color-border-default)] px-2 py-1 text-[12px] ${item === teamPage ? 'cursor-not-allowed bg-[var(--color-text-primary)] text-[var(--color-surface-card)]' : 'cursor-pointer bg-[var(--color-surface-card)] text-[var(--color-text-primary)]'}`}
                            >
                                {item}
                            </button>
                        )
                    )}
                </div>
                <button
                    onClick={() => setTeamPage((p) => Math.min(totalTeamPages, p + 1))}
                    disabled={teamPage >= totalTeamPages}
                    className={`rounded-[4px] border border-[var(--color-border-default)] bg-[var(--color-surface-card)] px-[10px] py-1 text-[12px] text-[var(--color-text-primary)] ${teamPage >= totalTeamPages ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                >
                    {t('次へ', 'Next', 'Siguiente')}
                </button>
            </div>
        );
    };

    const colors = TEAM_COLORS[teamSlug] || { main: '#ccc', sub: '#333' };

    return (
        <div className="team-page">
            <div className="team-header">
                <div className="breadcrumbs">
                    <Link href={withLocalePrefix(locale, '/')}>{t('ホーム', 'Home', 'Inicio')}</Link>
                    {' > '}
                    <Link href={withLocalePrefix(locale, `/${league}/`)}>
                        {leagueLabel === 'NPB' ? t('プロ野球（NPB）チーム一覧', 'Nippon Professional Baseball (NPB) Teams', 'Equipos de Béisbol Profesional Japonés (NPB)') : t('メジャーリーグ（MLB）チーム一覧', 'Major League Baseball (MLB) Teams', 'Equipos de Grandes Ligas (MLB)')}
                    </Link>
                    {' > '}
                    {teamName}
                </div>
                <h1
                    className="team-title"
                    style={{ '--team-main': colors.main, '--team-sub': colors.sub } as React.CSSProperties}
                >
                    {teamName}
                </h1>
            </div>

            <div className="filter-controls mb-[15px] rounded-[8px] bg-[var(--color-surface-muted)] px-5 py-[15px] text-[13px]">
                <div className="status-radios desktop-filter-row mb-3 flex flex-wrap items-center">
                    <span className="w-[100px] shrink-0 text-left font-semibold text-[var(--color-text-secondary)]">{t('対象', 'Roster', 'Roster')}</span>
                    {(['active', 'ob', 'all'] as const).map((s) => (
                        <label key={s} className="mr-5 flex cursor-pointer items-center">
                            <input type="radio" name="roster" value={s} checked={rosterStatus === s} onChange={() => setRosterStatus(s)} className="mr-1" />
                            {s === 'active' ? t('現役選手', 'Active', 'Activo') : s === 'ob' ? t('OB選手', 'OB', 'OB') : t('すべて', 'All', 'Todos')}
                        </label>
                    ))}
                </div>
                <div className="status-selects mobile-filter-row mb-3 w-full items-center">
                    <span className="w-[100px] shrink-0 font-semibold text-[var(--color-text-secondary)]">{t('対象', 'Roster', 'Roster')}</span>
                    <select
                        aria-label={t('対象', 'Roster', 'Roster')}
                        value={rosterStatus}
                        onChange={(e) => setRosterStatus(e.target.value as 'active' | 'ob' | 'all')}
                        className="flex-1 rounded-[4px] border border-[var(--color-border-default)] p-2 text-[13px]"
                    >
                        <option value="active">{t('現役選手', 'Active', 'Activo')}</option>
                        <option value="ob">{t('OB選手', 'OB', 'OB')}</option>
                        <option value="all">{t('すべて', 'All', 'Todos')}</option>
                    </select>
                </div>

                <div className="position-radios desktop-filter-row mb-3 flex flex-wrap items-center">
                    <span className="w-[100px] shrink-0 font-semibold text-[var(--color-text-secondary)]">{t('守備位置', 'Position', 'Posición')}</span>
                    {[...POSITION_OPTIONS, POS_ALL_LABEL].map((pos) => (
                        <label key={pos} className="mb-1 mr-[15px] flex cursor-pointer items-center">
                            <input type="radio" name="position" value={pos === POS_ALL_LABEL ? 'all' : pos} checked={selectedPosition === (pos === POS_ALL_LABEL ? 'all' : pos)} onChange={(e) => setSelectedPosition(e.target.value)} className="mr-1" />
                            {t(pos, pos === POS_ALL_LABEL ? 'All' : getPositionShortLabel(pos))}
                        </label>
                    ))}
                </div>
                <div className="position-selects mobile-filter-row mb-3 w-full items-center">
                    <span className="w-[100px] shrink-0 font-semibold text-[var(--color-text-secondary)]">{t('守備位置', 'Position', 'Posición')}</span>
                    <select
                        aria-label={t('守備位置', 'Position', 'Posición')}
                        value={selectedPosition}
                        onChange={(e) => setSelectedPosition(e.target.value)}
                        className="flex-1 rounded-[4px] border border-[var(--color-border-default)] p-2 text-[13px]"
                    >
                        {[...POSITION_OPTIONS, POS_ALL_LABEL].map((pos) => (
                            <option key={pos} value={pos === POS_ALL_LABEL ? 'all' : pos}>
                                {t(pos, pos === POS_ALL_LABEL ? 'All' : getPositionShortLabel(pos))}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="text-filter flex flex-wrap items-center">
                    <span className="w-[100px] shrink-0 font-semibold text-[var(--color-text-secondary)]">{t('選手名', 'Name', 'Nombre')}</span>
                    <input
                        type="text"
                        placeholder={t('選手名で絞り込み...', 'Filter by player name...', 'Filtrar por nombre...')}
                        value={filterQuery}
                        onChange={(e) => setFilterQuery(e.target.value)}
                        onCompositionStart={() => setIsComposing(true)}
                        onCompositionEnd={(e) => { setIsComposing(false); setFilterQuery(e.currentTarget.value); }}
                        className="filter-input m-0 max-w-[300px] flex-1 rounded-[4px] text-[13px]"
                    />
                </div>
            </div>

            <div className="player-list">
                {sortedPlayers.length === 0 ? (
                    <PageEmptyState
                        id="team-empty-state"
                        className="team-empty-state"
                        title={hasRosterData
                          ? t('該当する選手がいません。', 'No players matched these filters.', 'No se encontraron jugadores para estos filtros.')
                          : t('選手データがありません。', 'No roster data is available.', 'No hay datos de plantilla disponibles.')}
                        paragraphs={[
                            hasRosterData
                              ? t(
                                  'この条件では選手が 0 人でした。',
                                  'No players matched the current filters.',
                                  'No hubo jugadores que coincidieran con los filtros actuales.',
                                )
                              : t(
                                  'この球団の選手データはありません。',
                                  'No roster data is available for this team.',
                                  'No hay datos de plantilla disponibles para este equipo.',
                                ),
                        ]}
                        links={emptyStateLinks}
                        linksLabel={t('関連リンク', 'Related links', 'Enlaces relacionados')}
                    />
                ) : (
                    <>
                        <div className="player-count">
                            {t(
                                `${sortedPlayers.length}選手見つかりました（${teamPageStart}-${teamPageEnd}件表示）`,
                                `${sortedPlayers.length} players found (showing ${teamPageStart}-${teamPageEnd})`,
                                `${sortedPlayers.length} jugadores (mostrando ${teamPageStart}-${teamPageEnd})`
                            )}
                        </div>
                        {renderPagination('top')}
                        <table className="player-table">
                            <thead>
                                <tr>
                                    <th className="col-uniform sortable-th" aria-sort={getAriaSort('uniformNumber')}>
                                        <button type="button" className={`sort-button ${sortField === 'uniformNumber' ? 'active' : ''}`} onClick={() => handleSort('uniformNumber')}>
                                            <span>{t('背番号', 'No.', 'N.º')}</span>
                                            <span className="sort-indicator" aria-hidden="true">{getSortIndicator('uniformNumber')}</span>
                                        </button>
                                    </th>
                                    <th className="col-name sortable-th" aria-sort={getAriaSort('name')}>
                                        <button type="button" className={`sort-button ${sortField === 'name' ? 'active' : ''}`} onClick={() => handleSort('name')}>
                                            <span>{t('選手名', 'Name', 'Nombre')}</span>
                                            <span className="sort-indicator" aria-hidden="true">{getSortIndicator('name')}</span>
                                        </button>
                                    </th>
                                    <th className="col-desktop sortable-th" aria-sort={getAriaSort('position')}>
                                        <button type="button" className={`sort-button ${sortField === 'position' ? 'active' : ''}`} onClick={() => handleSort('position')}>
                                            <span>{t('ポジション', 'Position', 'Posición')}</span>
                                            <span className="sort-indicator" aria-hidden="true">{getSortIndicator('position')}</span>
                                        </button>
                                    </th>
                                    <th className="col-desktop sortable-th" aria-sort={getAriaSort('nationality')}>
                                        <button type="button" className={`sort-button ${sortField === 'nationality' ? 'active' : ''}`} onClick={() => handleSort('nationality')}>
                                            <span>{t('国籍', 'Nationality', 'Nacionalidad')}</span>
                                            <span className="sort-indicator" aria-hidden="true">{getSortIndicator('nationality')}</span>
                                        </button>
                                    </th>
                                    <th className="col-desktop sortable-th" aria-sort={getAriaSort('birthday')}>
                                        <button type="button" className={`sort-button ${sortField === 'birthday' ? 'active' : ''}`} onClick={() => handleSort('birthday')}>
                                            <span>{t('生年月日', 'Birthday', 'Fecha de nacimiento')}</span>
                                            <span className="sort-indicator" aria-hidden="true">{getSortIndicator('birthday')}</span>
                                        </button>
                                    </th>
                                    <th className="col-mobile sortable-th text-[0.9em] font-medium text-[var(--color-text-muted)]">
                                        <div className="mobile-sort-list">
                                            <button type="button" className={`mobile-sort-button ${sortField === 'position' ? 'active' : ''}`} onClick={() => handleSort('position')}>
                                                <span>{t('ポジション', 'Position', 'Posición')}</span>
                                                <span className="sort-indicator" aria-hidden="true">{getSortIndicator('position')}</span>
                                            </button>
                                            <button type="button" className={`mobile-sort-button ${sortField === 'nationality' ? 'active' : ''}`} onClick={() => handleSort('nationality')}>
                                                <span>{t('国籍', 'Nationality', 'Nacionalidad')}</span>
                                                <span className="sort-indicator" aria-hidden="true">{getSortIndicator('nationality')}</span>
                                            </button>
                                            <button type="button" className={`mobile-sort-button ${sortField === 'birthday' ? 'active' : ''}`} onClick={() => handleSort('birthday')}>
                                                <span>{t('生年月日', 'Birthday', 'Fecha de nacimiento')}</span>
                                                <span className="sort-indicator" aria-hidden="true">{getSortIndicator('birthday')}</span>
                                            </button>
                                        </div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {pagedPlayers.map((player) => (
                                    <tr key={player.profile_id}
                                        className="cursor-pointer"
                                        onClick={() => router.push(`${withLocalePrefix(locale, `/players/${player.slug}`)}?team=${teamSlug}`)}
                                    >
                                        <td className="col-uniform">{getUniformNumber(player) ?? ''}</td>
                                        <td className="col-name">
                                            <Link href={`${withLocalePrefix(locale, `/players/${player.slug}`)}?team=${teamSlug}`} className="player-link">
                                                {val(player.name, player.name_en)}
                                            </Link>
                                        </td>
                                        <td className="col-desktop">{getDisplayedPosition(player)}</td>
                                        <td className="col-desktop">{getDisplayedNationality(player)}</td>
                                        <td className="col-desktop">{formatBirthday(player.birthday_date, !!(player.death_date_date || player.death_date), locale)}</td>
                                        <td className="col-mobile">
                                            <div className="player-info-cell">
                                                <span className="player-info-value">{getDisplayedPosition(player)}</span>
                                                <span className="player-info-value">{getDisplayedNationality(player)}</span>
                                                <span className="player-info-value">{formatBirthday(player.birthday_date, !!(player.death_date_date || player.death_date), locale)}</span>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {renderPagination('bottom')}
                    </>
                )}
            </div>
        </div>
    );
}

function TeamWithSearchParams({ locale, teamSlug, league, initialPlayers }: Props) {
    const searchParams = useSearchParams();
    const getParam = (key: string): string | null => searchParams?.get(key) ?? null;
    const spv: SearchParamValues = {
        q: getParam('q') || '',
        pos: getParam('pos'),
        active: getParam('active'),
        ob: getParam('ob'),
    };
    return <TeamInner locale={locale} teamSlug={teamSlug} league={league} initialPlayers={initialPlayers} spv={spv} />;
}

export default function TeamClient({ locale, teamSlug, league, initialPlayers }: Props) {
    return (
        <Suspense fallback={
            <TeamInner locale={locale} teamSlug={teamSlug} league={league} initialPlayers={initialPlayers} spv={DEFAULT_SPV} />
        }>
            <TeamWithSearchParams locale={locale} teamSlug={teamSlug} league={league} initialPlayers={initialPlayers} />
        </Suspense>
    );
}
